import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useMemo,
} from 'react';
import {
  AnimatePresence,
  MotionConfig,
  motion,
  useMotionValue,
  useDragControls,
  useTransform,
  type PanInfo,
  type Variants,
} from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  scenesFor,
  type Drift,
  type TourEnding,
  type TourTopic,
} from './tourScenes';
import { ReactComponent as CheckIcon } from '../../icons/check.svg';
import './feature-tour.scss';

const QUEUED_MS = 1100;
const SWIPE_PX = 56;
const SWIPE_VELOCITY = 450;

export const EASE_OUT = [0.22, 1, 0.36, 1] as const;

/** Mirrors the desktop block in feature-tour.scss: the 16:9 floating stage. */
const DESKTOP_QUERY = '(min-width: 760px) and (min-height: 480px)';

/** Card height of the full-size desktop stage; desktop poses are in px at
 *  this size and scale with the stage below it. */
const DESKTOP_CARD_H = 720;

/** Mirrors the desktop stage background in feature-tour.scss. */
const STAGE_BLACK = '#070a12';
const STAGE_CLEAR = 'rgba(7, 10, 18, 0)';

const useDesktop = () => {
  const [desktop, setDesktop] = useState(
    () =>
      typeof window.matchMedia === 'function' &&
      window.matchMedia(DESKTOP_QUERY).matches,
  );
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(DESKTOP_QUERY);
    const onChange = (e: MediaQueryListEvent) => setDesktop(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return desktop;
};

// Each photo is revealed by a circle growing out from a scene-specific origin.
export const photoVariants: Variants = {
  enter: (origin: string) => ({
    clipPath: `circle(0% at ${origin})`,
    opacity: 1,
    scale: 1,
  }),
  centre: (origin: string) => ({
    clipPath: `circle(150% at ${origin})`,
    opacity: 1,
    scale: 1,
    transition: { clipPath: { duration: 1.15, ease: EASE_OUT } },
  }),
  exit: {
    opacity: 0,
    scale: 1.08,
    transition: { duration: 0.9, ease: 'easeOut', delay: 0.25 },
  },
};

export const driftTransition = {
  duration: 9,
  ease: [0.16, 1, 0.3, 1] as const,
};

/** The photo's slow settle after the reveal, different for every scene. */
export const driftFrom = ({ x, y, scale }: Drift) => ({
  x: `${x}%`,
  y: `${y}%`,
  scale,
});

// The leaving block is inert so a quick second tap cannot land on it.
export const copyVariants: Variants = {
  enter: {
    pointerEvents: 'auto',
    transition: { staggerChildren: 0.055, delayChildren: 0.18 },
  },
  exit: {
    pointerEvents: 'none',
    transition: { staggerChildren: 0.025, staggerDirection: -1 },
  },
};

export const lineVariants: Variants = {
  initial: { opacity: 0, y: 26, filter: 'blur(6px)' },
  enter: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: { duration: 0.62, ease: EASE_OUT },
  },
  exit: {
    opacity: 0,
    y: -14,
    filter: 'blur(4px)',
    transition: { duration: 0.26, ease: 'easeIn' },
  },
};

export const wordVariants: Variants = {
  initial: { y: '112%', rotate: 3 },
  enter: {
    y: 0,
    rotate: 0,
    transition: { duration: 0.72, ease: EASE_OUT },
  },
  exit: { y: '-115%', transition: { duration: 0.24, ease: 'easeIn' } },
};

/** A headline line, word by word, each rising out of its own clip. */
export const Words: React.FC<{ text: string; className?: string }> = ({
  text,
  className,
}) => (
  <span className={className}>
    {text.split(' ').map((word, i) => (
      <span key={`${word}-${i}`} className='feature-tour__clip'>
        <motion.span className='feature-tour__word' variants={wordVariants}>
          {word}
        </motion.span>
      </span>
    ))}
  </span>
);

const Tick: React.FC = () => (
  <motion.svg
    viewBox='0 0 16 16'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'
  >
    <motion.path
      d='M3 8.5l3.2 3.2L13 4.8'
      initial={{ pathLength: 0 }}
      animate={{ pathLength: 1 }}
      transition={{ duration: 0.4, delay: 0.1, ease: EASE_OUT }}
    />
  </motion.svg>
);

const Arrow: React.FC = () => (
  <svg
    viewBox='0 0 16 16'
    fill='none'
    stroke='currentColor'
    strokeWidth='1.75'
    aria-hidden='true'
  >
    <path
      d='M3 8h10M9 4l4 4-4 4'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

export interface MoreCopy {
  icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  /** A concrete situation where the feature helps. */
  example: string;
  exchange: { from: 'me' | 'pa'; text: string }[];
  /** Follows the exchange. */
  note?: string;
  benefits: string[];
}

export interface FeatureTourProps {
  /** Which scene ends the tour (the backend picks it per user). */
  ending: TourEnding;
  /** Names the assistant in the intro; falls back to "Dream". */
  assistantName: string | null;
  /** Scene ids already queued (a reload mid-tour keeps them). */
  queued?: string[];
  /** Where the tour sits: after the Morning List's cards, or on its own page. */
  context: 'briefing' | 'standalone';
  /** Every queued topic so far, each time one is added. */
  onQueue: (topics: TourTopic[]) => void;
  /** The user reached the end; `topics` is the full queued set. */
  onDone: (topics: TourTopic[]) => void;
  /** Standalone only: Back to WhatsApp on the summary card. */
  onBack?: () => void;
  /** Prototype: "Tell me more" opens a scrollable card with a fuller
   *  explainer and "Ok, thanks" instead of queueing a topic. */
  more?: {
    copy: (sceneId: string) => MoreCopy | null;
  };
}

/**
 * The tour of what's new for people who joined before the new onboarding:
 * the onboarding's chapter screen (full-bleed photo and two-tone display
 * headline), one feature per scene, with "Show me after" or "I'm not interested
 * in this".
 */
const FeatureTour: React.FC<FeatureTourProps> = ({
  ending,
  assistantName,
  queued: initialQueued = [],
  context,
  onQueue,
  onDone,
  onBack,
  more,
}) => {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  const [queued, setQueued] = useState<string[]>(initialQueued);
  const [justQueued, setJustQueued] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const moreDrag = useDragControls();
  const timer = useRef<number | undefined>(undefined);
  const dragX = useMotionValue(0);
  const photoX = useTransform(dragX, (v) => v * 0.28);

  const desktop = useDesktop();
  const stageRef = useRef<HTMLDivElement>(null);
  const [stageH, setStageH] = useState(0);
  useLayoutEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    setStageH(el.offsetHeight);
    const ro = new ResizeObserver(([entry]) =>
      setStageH(entry.contentRect.height),
    );
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const tour = useMemo(
    () => scenesFor(ending, context === 'standalone'),
    [ending, context],
  );
  const slide = tour[index];
  const summary = slide.id === 'summary';
  const name = assistantName || 'Dream';
  const copy = (key: string) =>
    t(`tour.scenes.${slide.id}.${key}`, { name, count: features });
  const last = index === tour.length - 1;
  const features = tour.filter((s) => s.feature).length;
  const featureNo = tour.slice(0, index + 1).filter((s) => s.feature).length;
  const wasQueued = slide.feature && queued.includes(slide.id);
  const moreCopy = more && slide.feature ? more.copy(slide.id) : null;
  const image = (desktop && slide.desktop?.image) || slide.image;
  const photoStyle = desktop
    ? {
        left:
          (slide.desktop?.shift ?? 0) * (stageH ? stageH / DESKTOP_CARD_H : 1),
      }
    : {
        objectPosition: `${slide.focusX ?? '50%'} ${slide.focusY ?? '50%'}`,
      };
  const topicsOf = (ids: string[]): TourTopic[] =>
    tour
      .filter((s) => s.feature && ids.includes(s.id))
      .map((s) => ({ id: s.id, title: t(`tour.scenes.${s.id}.topic`) }));

  useEffect(() => {
    tour.forEach((scene) => {
      new Image().src = (desktop && scene.desktop?.image) || scene.image;
    });
  }, [tour, desktop]);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const go = (next: number) => {
    if (next < 0 || next >= tour.length || next === index) return;
    window.clearTimeout(timer.current);
    setJustQueued(false);
    setShowMore(false);
    setIndex(next);
  };

  const advance = (ids: string[]) => {
    if (last || tour[index + 1]?.id === 'summary') onDone(topicsOf(ids));
    if (!last) go(index + 1);
  };

  const chat = () => {
    if (justQueued || !slide.feature) return;
    const ids = queued.includes(slide.id) ? queued : [...queued, slide.id];
    if (ids !== queued) onQueue(topicsOf(ids));
    setQueued(ids);
    setJustQueued(true);
    timer.current = window.setTimeout(() => advance(ids), QUEUED_MS);
  };

  const primary = () => {
    if (moreCopy && !showMore) setShowMore(true);
    else chat();
  };
  const closeMore = () => setShowMore(false);
  const onMoreDragEnd = (_: unknown, info: PanInfo) => {
    if (info.offset.y > 90 || info.velocity.y > 500) closeMore();
  };

  useEffect(() => {
    if (!desktop) return;
    const onKey = (e: KeyboardEvent) => {
      if (showMore) {
        if (e.key === 'Escape') closeMore();
        return;
      }
      if (e.key === 'ArrowRight') go(index + 1);
      else if (e.key === 'ArrowLeft') go(index - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const onDragEnd = (_: unknown, info: PanInfo) => {
    const { offset, velocity } = info;
    if (offset.x < -SWIPE_PX || velocity.x < -SWIPE_VELOCITY) go(index + 1);
    else if (offset.x > SWIPE_PX || velocity.x > SWIPE_VELOCITY) go(index - 1);
  };

  return (
    <MotionConfig reducedMotion='user'>
      <motion.section
        className='feature-tour'
        aria-roledescription='carousel'
        aria-label={t('tour.carouselAria')}
        data-testid='feature-tour'
        // Desktop: the page behind dims to the black stage and the card rises
        // onto it, instead of both cutting in at once.
        initial={desktop ? { backgroundColor: STAGE_CLEAR } : false}
        animate={desktop ? { backgroundColor: STAGE_BLACK } : undefined}
        transition={{ duration: 0.9, ease: 'easeInOut' }}
      >
        <div className='feature-tour__stage'>
          <motion.div
            className='feature-tour__photo-stage'
            ref={stageRef}
            initial={desktop ? { opacity: 0, y: 48, scale: 0.94 } : false}
            animate={desktop ? { opacity: 1, y: 0, scale: 1 } : undefined}
            transition={{ duration: 1.2, delay: 0.3, ease: EASE_OUT }}
          >
            <motion.div
              className='feature-tour__photo-stage-inner'
              initial={{ opacity: 0, scale: 1.08, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 1.4, ease: EASE_OUT }}
            >
              <AnimatePresence custom={slide.photoOrigin}>
                <motion.div
                  key={slide.id}
                  className='feature-tour__photo'
                  custom={slide.photoOrigin}
                  variants={photoVariants}
                  initial='enter'
                  animate='centre'
                  exit='exit'
                  style={{ x: photoX }}
                >
                  <motion.img
                    src={image}
                    alt=''
                    draggable={false}
                    initial={driftFrom(slide.drift)}
                    animate={{ x: '0%', y: '0%', scale: 1 }}
                    transition={driftTransition}
                    style={photoStyle}
                  />
                </motion.div>
              </AnimatePresence>
              <div className='feature-tour__fade' aria-hidden='true' />
            </motion.div>
          </motion.div>

          <motion.header
            className='feature-tour__top'
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.7,
              delay: desktop ? 0.9 : 0.45,
              ease: EASE_OUT,
            }}
          >
            <span className='feature-tour__label'>
              <span className='feature-tour__dot' aria-hidden='true' />
              {t('tour.whatsNew')}
              {slide.feature && (
                <>
                  <span className='feature-tour__sep' aria-hidden='true' />
                  <strong>
                    {t('tour.ofCount', { n: featureNo, total: features })}
                  </strong>
                </>
              )}
            </span>
            <ol className='feature-tour__progress' aria-hidden='true'>
              {tour.map((s, i) => (
                <li key={s.id} className='feature-tour__seg'>
                  <motion.span
                    className={
                      s.feature && queued.includes(s.id)
                        ? 'feature-tour__seg-fill feature-tour__seg-fill--queued'
                        : 'feature-tour__seg-fill'
                    }
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: i <= index ? 1 : 0 }}
                    transition={{ duration: 0.6, ease: EASE_OUT }}
                  />
                </li>
              ))}
            </ol>
            <AnimatePresence>
              {index > 0 && (
                <motion.button
                  type='button'
                  className='feature-tour__back'
                  aria-label={t('tour.previous')}
                  onClick={() => go(index - 1)}
                  initial={{ opacity: 0, x: -6 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -6 }}
                  transition={{ duration: 0.35, ease: EASE_OUT }}
                >
                  <svg
                    viewBox='0 0 16 16'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='1.75'
                    aria-hidden='true'
                  >
                    <path
                      d='M10 3L5 8l5 5'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </motion.button>
              )}
            </AnimatePresence>
          </motion.header>

          <motion.div
            className='feature-tour__sheet'
            initial={desktop ? { opacity: 0, y: 16 } : false}
            animate={desktop ? { opacity: 1, y: 0 } : undefined}
            transition={{ duration: 0.8, delay: 0.75, ease: EASE_OUT }}
            drag={desktop || showMore ? false : 'x'}
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.18}
            dragMomentum={false}
            style={{ x: dragX }}
            onDragEnd={onDragEnd}
          >
            <AnimatePresence mode='wait'>
              <motion.div
                key={slide.id}
                className={`feature-tour__copy${slide.feature ? '' : ' feature-tour__copy--hero'}`}
                variants={copyVariants}
                initial='initial'
                animate='enter'
                exit='exit'
                aria-roledescription='slide'
                aria-label={`${index + 1} of ${tour.length}: ${copy('name')}`}
              >
                <div className='feature-tour__text'>
                  <h1 className='feature-tour__display'>
                    <Words
                      text={copy('headline')}
                      className='feature-tour__line'
                    />
                    <Words
                      text={copy('tail')}
                      className='feature-tour__line feature-tour__line--tail'
                    />
                  </h1>
                  <motion.p
                    className='feature-tour__lede'
                    variants={lineVariants}
                  >
                    {copy('line')}
                  </motion.p>
                  {summary && (
                    <motion.ul
                      className='feature-tour__covered'
                      variants={lineVariants}
                      data-testid='feature-tour-covered'
                    >
                      {tour
                        .filter((s) => s.feature)
                        .map((s) => {
                          const on = queued.includes(s.id);
                          return (
                            <li
                              key={s.id}
                              className={`feature-tour__covered-item${on ? ' feature-tour__covered-item--on' : ''}`}
                            >
                              <span
                                className='feature-tour__covered-mark'
                                aria-hidden='true'
                              >
                                {on && <Tick />}
                              </span>
                              <span>{t(`tour.scenes.${s.id}.name`)}</span>
                            </li>
                          );
                        })}
                    </motion.ul>
                  )}
                </div>
                <motion.div
                  className='feature-tour__actions'
                  variants={lineVariants}
                >
                  {slide.feature ? (
                    <>
                      <motion.button
                        type='button'
                        className={`feature-tour__chat${justQueued || wasQueued ? ' feature-tour__chat--queued' : ''}`}
                        aria-live='polite'
                        whileTap={{ scale: 0.96 }}
                        layout
                        transition={{
                          layout: { duration: 0.35, ease: EASE_OUT },
                        }}
                        onClick={primary}
                      >
                        <AnimatePresence mode='wait' initial={false}>
                          <motion.span
                            key={
                              justQueued
                                ? 'added'
                                : wasQueued
                                  ? 'again'
                                  : 'chat'
                            }
                            className='feature-tour__chat-inner'
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.22, ease: EASE_OUT }}
                          >
                            {justQueued ? (
                              <>
                                <Tick />
                                {t(
                                  context === 'briefing'
                                    ? 'tour.addedAfterList'
                                    : 'tour.addedForWhatsApp',
                                )}
                              </>
                            ) : wasQueued ? (
                              <>
                                <Tick />
                                {t('tour.added')}
                              </>
                            ) : (
                              <>
                                {t('tour.showMeAfter')}
                                <Arrow />
                              </>
                            )}
                          </motion.span>
                        </AnimatePresence>
                      </motion.button>
                      <motion.button
                        type='button'
                        className='feature-tour__ok'
                        disabled={justQueued}
                        whileTap={{ scale: 0.96 }}
                        animate={{ opacity: justQueued ? 0.45 : 1 }}
                        onClick={() => advance(queued)}
                      >
                        {t('tour.notInterested')}
                      </motion.button>
                    </>
                  ) : (
                    <motion.button
                      type='button'
                      className={`feature-tour__chat${
                        summary ? ' feature-tour__chat--pill' : ''
                      }`}
                      whileTap={{ scale: 0.96 }}
                      onClick={() => (summary ? onBack?.() : go(index + 1))}
                    >
                      <span className='feature-tour__chat-inner'>
                        {t(
                          summary ? 'tour.openWhatsApp' : 'tour.startExploring',
                        )}
                        <Arrow />
                      </span>
                    </motion.button>
                  )}
                </motion.div>
              </motion.div>
            </AnimatePresence>
          </motion.div>
          <AnimatePresence>
            {showMore && moreCopy && (
              <motion.div
                key='more'
                className='feature-tour__more-layer'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
              >
                <div
                  className='feature-tour__more-backdrop'
                  onClick={closeMore}
                  aria-hidden='true'
                />
                <motion.div
                  className='feature-tour__more-card'
                  role='dialog'
                  aria-modal='true'
                  aria-label={copy('name')}
                  data-testid='feature-tour-more'
                  initial={{ y: '100%' }}
                  animate={{ y: 0 }}
                  exit={{ y: '100%' }}
                  transition={{ type: 'spring', damping: 32, stiffness: 320 }}
                  drag='y'
                  dragListener={false}
                  dragControls={moreDrag}
                  dragConstraints={{ top: 0, bottom: 0 }}
                  dragElastic={{ top: 0, bottom: 0.6 }}
                  onDragEnd={onMoreDragEnd}
                >
                  <div
                    className='feature-tour__more-grab'
                    onPointerDown={(e) => moreDrag.start(e)}
                  >
                    <span className='feature-tour__more-handle' />
                  </div>
                  <button
                    type='button'
                    className='feature-tour__more-close'
                    aria-label={t('tour.okThanks')}
                    onClick={closeMore}
                  >
                    <svg viewBox='0 0 24 24' aria-hidden='true'>
                      <path
                        d='M6 6l12 12M18 6L6 18'
                        fill='none'
                        stroke='currentColor'
                        strokeWidth='2'
                        strokeLinecap='round'
                      />
                    </svg>
                  </button>
                  <div className='feature-tour__more-scroll'>
                    <div className='feature-tour__more-body'>
                      <div className='feature-tour__more-head'>
                        <span className='feature-tour__more-icon'>
                          <moreCopy.icon aria-hidden='true' />
                        </span>
                        <span className='feature-tour__more-name'>
                          {copy('name')}
                        </span>
                      </div>
                      <p className='feature-tour__more-example'>
                        {moreCopy.example}
                      </p>
                      <div className='feature-tour__more-chat'>
                        {moreCopy.exchange.map((b) => (
                          <div
                            key={b.text}
                            className={`feature-tour__bubble feature-tour__bubble--${b.from}`}
                          >
                            {b.from === 'pa' && (
                              <span className='feature-tour__bubble-who'>
                                {name}
                              </span>
                            )}
                            {b.text}
                          </div>
                        ))}
                      </div>
                      {moreCopy.note && (
                        <p className='feature-tour__more-example'>
                          {moreCopy.note}
                        </p>
                      )}
                      <ul className='feature-tour__more-benefits'>
                        {moreCopy.benefits.map((b) => (
                          <li key={b}>
                            <CheckIcon aria-hidden='true' />
                            {b}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className='feature-tour__more-foot'>
                    <motion.button
                      type='button'
                      className='feature-tour__more-ok'
                      whileTap={{ scale: 0.96 }}
                      onClick={() => advance(queued)}
                      data-testid='feature-tour-ok-thanks'
                    >
                      {t('tour.okThanks')}
                    </motion.button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
          {justQueued && (
            <motion.div
              className='feature-tour__pulse'
              aria-hidden='true'
              initial={{ opacity: 0.45, scale: 0.2 }}
              animate={{ opacity: 0, scale: 2.6 }}
              transition={{ duration: 0.9, ease: 'easeOut' }}
            />
          )}
        </div>
      </motion.section>
    </MotionConfig>
  );
};

export default FeatureTour;
